import { z } from 'zod';

/**
 * The error envelope every backend failure uses.
 *
 * Mirrors `app.core.errors.build_error_response`. Because the shape is
 * guaranteed server-side, the client needs exactly one parser and every screen
 * can rely on `code`/`message` being present.
 */
export const apiErrorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.string(), z.unknown()).default({}),
    request_id: z.string().nullable().default(null),
  }),
});

export type ApiErrorEnvelope = z.infer<typeof apiErrorEnvelopeSchema>;

/** Field-level validation failures, keyed by field path. */
export type FieldErrors = Record<string, string>;

/** A structured failure from the API, safe to surface to a user. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;
  readonly requestId: string | null;

  constructor(options: {
    status: number;
    code: string;
    message: string;
    details?: Record<string, unknown>;
    requestId?: string | null;
  }) {
    super(options.message);
    this.name = 'ApiError';
    this.status = options.status;
    this.code = options.code;
    this.details = options.details ?? {};
    this.requestId = options.requestId ?? null;
  }

  /** The user is not signed in, or their session expired. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }

  /** Server-side or upstream failure: retrying may help. */
  get isRetryable(): boolean {
    return this.status === 429 || this.status >= 500;
  }

  /** Seconds to wait before retrying, when the server told us. */
  get retryAfterSeconds(): number | null {
    const value = this.details.retry_after_seconds;
    return typeof value === 'number' ? value : null;
  }

  /**
   * Per-field messages from a 422, ready to attach to form inputs.
   */
  get fieldErrors(): FieldErrors {
    const fields = this.details.fields;
    if (typeof fields !== 'object' || fields === null) return {};

    const result: FieldErrors = {};
    for (const [key, value] of Object.entries(fields)) {
      if (typeof value === 'string') result[key] = value;
    }
    return result;
  }
}

/** A request that never reached the server (offline, DNS, CORS, timeout). */
export class NetworkError extends Error {
  override readonly cause: unknown;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

/** The server replied, but not in the shape the caller's schema requires. */
export class ResponseShapeError extends Error {
  readonly issues: string[];

  constructor(message: string, issues: string[]) {
    super(message);
    this.name = 'ResponseShapeError';
    this.issues = issues;
  }
}

/**
 * A message worth showing a user for any thrown value.
 *
 * Deliberately total: it accepts `unknown` so callers in `catch` blocks never
 * have to narrow before rendering something helpful.
 */
export function toUserMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof NetworkError) {
    return 'We could not reach the server. Check your connection and try again.';
  }
  if (error instanceof ResponseShapeError) {
    return 'The server sent an unexpected response. Please try again.';
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Something went wrong. Please try again.';
}
